import { TCheckOrganizationNameDto, TCheckOrganizationNameResDto } from "@/api/services/admin/organization/dtos/check-organization-name.dto";
import {
  TCreateOrganizationInitialUserReqDto,
  TCreateOrganizationInitialUserResDto,
} from "@/api/services/admin/organization/dtos/create-organization-initial-user.dto";
import { TCreateOrganizationReqDto, TCreateOrganizationResDto } from "@/api/services/admin/organization/dtos/create-organization.dto";
import { TUpdateOrganizationReqDto, TUpdateOrganizationResDto } from "@/api/services/admin/organization/dtos/update-organization.dto";
import {
  TGetOrganizationDetailResDto,
  TGetOrganizationUserListReqDto,
  TGetOrganizationUserListResDto,
} from "@/api/services/admin/organization/dtos/get-organization-detail.dto";
import {
  TGetOrganizationProductUserListReqDto,
  TGetOrganizationProductUserListResDto,
  TOrganizationProductUserDto,
} from "@/api/services/admin/organization/dtos/get-organization-product-user-list.dto";
import { TGetOrganizationListReqDto, TGetOrganizationListResDto } from "@/api/services/admin/organization/dtos/get-organization-list.dto";
import { TGetAssignableUsersResDto } from "@/api/services/admin/organization/dtos/get-assignable-users.dto";
import { TToggleOrganizationPinResDto } from "@/api/services/admin/organization/dtos/toggle-organization-pin.dto";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { USER_TYPE_KEYS } from "@/features/organization/constants/organizationDetail";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll";
import { TOption } from "@/types/common";
import { clientApi } from "@/utils/api";
import { createUrlWithParams } from "@/utils/url";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { useParams } from "react-router-dom";

export const fetchOrganizationListApi = async (req: TGetOrganizationListReqDto): Promise<TGetOrganizationListResDto> => {
  const url = createUrlWithParams("/organizations", req);
  const rs = await clientApi.get<TGetOrganizationListResDto>(url);
  return rs.data;
};

export const useOrganizationList = (req: TGetOrganizationListReqDto) => {
  return useQuery({
    queryKey: [QUERY_KEYS.ORGANIZATION.FETCH_LIST, req],
    queryFn: () => fetchOrganizationListApi(req),
  });
};

export const toggleOrganizationPinApi = async (id: number): Promise<TToggleOrganizationPinResDto> => {
  const rs = await clientApi.patch<TToggleOrganizationPinResDto>(`/organizations/${id}/pin`);
  return rs.data;
};

export const useToggleOrganizationPin = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => toggleOrganizationPinApi(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORGANIZATION.FETCH_LIST] });
    },
  });
};

export const createOrganizationApi = async (payload: TCreateOrganizationReqDto): Promise<TCreateOrganizationResDto> => {
  const rs = await clientApi.post<TCreateOrganizationResDto>("/organizations", { ...payload, productIds: payload.productIds?.map(Number) });
  return rs.data;
};

export const checkOrganizationNameDuplicateApi = async (name: string): Promise<TCheckOrganizationNameDto> => {
  const url = createUrlWithParams("/organizations/check-name", { name: name.trim() });
  const rs = await clientApi.get<TCheckOrganizationNameResDto>(url);
  return rs.data.data;
};

export const updateOrganizationApi = async (id: number, payload: TUpdateOrganizationReqDto): Promise<TUpdateOrganizationResDto> => {
  const rs = await clientApi.put<TUpdateOrganizationResDto>(`/organizations/${id}`, payload);
  return rs.data;
};

export const fetchOrganizationDetailApi = async (id: string): Promise<TGetOrganizationDetailResDto> => {
  const rs = await clientApi.get<TGetOrganizationDetailResDto>(`/organizations/${id}`);
  return rs.data;
};

export const useOrganizationDetail = () => {
  const { id } = useParams();
  return useQuery({
    queryKey: [QUERY_KEYS.ORGANIZATION.FETCH_DETAIL, id],
    queryFn: () => (id ? fetchOrganizationDetailApi(id) : undefined),
    enabled: Boolean(id),
  });
};

// Candidate users for the 担当者 (person-in-charge) dropdown: active, non-admin-linked users of the org.
export const fetchAssignableUsersApi = async (orgId: number): Promise<TGetAssignableUsersResDto> => {
  const rs = await clientApi.get<TGetAssignableUsersResDto>(`/organizations/${orgId}/assignable-users`);
  return rs.data;
};

export const useActiveOrgUsers = (orgId?: number) => {
  const { id: paramId } = useParams();
  // Prefer explicit orgId; fall back to the route param (edit route has :id).
  const resolvedId = orgId ?? (paramId ? Number(paramId) : undefined);
  const { data } = useQuery({
    queryKey: [QUERY_KEYS.ORGANIZATION.FETCH_ASSIGNABLE_USERS, resolvedId],
    queryFn: () => (resolvedId ? fetchAssignableUsersApi(resolvedId) : undefined),
    enabled: Boolean(resolvedId),
  });
  const options = useMemo<TOption[]>(() => (data?.data ?? []).map((u) => ({ label: u.fullName, value: String(u.id) })), [data]);
  return { options };
};

export const fetchOrganizationUserListApi = async (orgId: string, req: TGetOrganizationUserListReqDto): Promise<TGetOrganizationUserListResDto> => {
  const url = createUrlWithParams(`/users/organizations/${orgId}`, req);
  const rs = await clientApi.get<TGetOrganizationUserListResDto>(url);
  return rs.data;
};

export const fetchOrganizationAdminUserListApi = async (
  orgId: string,
  req: Omit<TGetOrganizationUserListReqDto, "type">,
): Promise<TGetOrganizationUserListResDto> => {
  return fetchOrganizationUserListApi(orgId, { ...req, type: USER_TYPE_KEYS.ADMIN });
};

export const useOrganizationUserList = (req: Omit<TGetOrganizationUserListReqDto, "type">, orgId?: string) => {
  const { id: paramId } = useParams();
  // Prefer the explicitly passed orgId (e.g. carried via router state); fall back to the route param.
  const id = orgId ?? paramId;
  return useQuery({
    queryKey: [QUERY_KEYS.ORGANIZATION_USER.FETCH_LIST, "users", id, req],
    queryFn: () => (id ? fetchOrganizationUserListApi(id, req) : undefined),
    enabled: Boolean(id),
  });
};

// Product users of an organization + the current logged-in admin as the first row of page 1.
export const fetchOrganizationProductUserListApi = async (
  orgId: string,
  req: TGetOrganizationProductUserListReqDto,
): Promise<TGetOrganizationProductUserListResDto> => {
  const url = createUrlWithParams(`/users/organizations/${orgId}/product-users`, req);
  const rs = await clientApi.get<TGetOrganizationProductUserListResDto>(url);
  return rs.data;
};

export const useOrganizationProductUserListInfinite = (orgId?: string, req?: TGetOrganizationProductUserListReqDto) => {
  const { id: paramId } = useParams();
  const id = orgId ?? paramId;
  return useInfiniteScroll<TOrganizationProductUserDto>(
    [QUERY_KEYS.ORGANIZATION_USER.FETCH_PRODUCT_USER_LIST, "infinite", id, req],
    (page) => fetchOrganizationProductUserListApi(id as string, { ...req, page }),
    { enabled: Boolean(id) },
  );
};

export const useOrganizationAdminUserList = (req: Omit<TGetOrganizationUserListReqDto, "type">) => {
  const { id } = useParams();
  return useQuery({
    queryKey: [QUERY_KEYS.ORGANIZATION_USER.FETCH_ADMIN_LIST, "admin-users", id, req],
    queryFn: () => (id ? fetchOrganizationAdminUserListApi(id, req) : undefined),
    enabled: Boolean(id),
  });
};

export const deleteOrganizationApi = async (id: number): Promise<{ data: true }> => {
  const rs = await clientApi.delete<{ data: true }>(`/organizations/${id}`);
  return rs.data;
};

export const archiveOrganizationApi = async (id: number): Promise<{ data: true }> => {
  const rs = await clientApi.patch<{ data: true }>(`/organizations/${id}/archive`);
  return rs.data;
};

export const unarchiveOrganizationApi = async (id: number): Promise<{ data: true }> => {
  const rs = await clientApi.patch<{ data: true }>(`/organizations/${id}/unarchive`);
  return rs.data;
};

export const deleteOrganizationUsersApi = async (ids: number[]): Promise<{ data: true }> => {
  const rs = await clientApi.delete<{ data: true }>("/users", { data: { ids } });
  return rs.data;
};

export const createOrganizationInitialUserApi = async (
  organizationId: string,
  payload: TCreateOrganizationInitialUserReqDto,
): Promise<TCreateOrganizationInitialUserResDto> => {
  const rs = await clientApi.post<TCreateOrganizationInitialUserResDto>(`/organizations/${organizationId}/initial-user`, payload);
  return rs.data;
};
